import { InputAdornment, TextField } from '@material-ui/core';
import { debounce, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';

import {
  convertStringToCurrencySymbol,
  convertStringToECurrencyType,
  CurrencyType,
  CurrencyTypeUSD,
  fetchExchangeRates,
  IExchangeRate,
} from '~/src/dash/api/exchange-rates';
import { Col, Cols, Span, Text, usePanel } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { SingleSelect } from '~/src/dash/views/singleselect-view';

interface IExchangeRates {
  [key: number]: IExchangeRate;
}

interface ICurrency {
  id: number;
  label: string; // ex: 'US Dollar'
  value: CurrencyType; // ex; 'USD'
}

interface ICurrencyConverterProps {
  currencies: Array<ICurrency>;
  price: string; // FIXME: we need to use string cause the parent component use this param as a string
  currency: CurrencyType;
  onChange: ({
    price,
    uiPrice,
    uiCurrency,
  }: {
    price: string;
    uiPrice: string;
    uiCurrency: CurrencyType;
  }) => void;
  className: string;
  isDisabled?: boolean;
  isErrored?: boolean;
  isInChannels?: boolean; // if true, this component is used on the Channel page
}

export const CurrencyConverter = ({
  currencies,
  price,
  currency,
  onChange,
  className, // for cypress testing
  isDisabled,
  isErrored,
  isInChannels,
}: ICurrencyConverterProps) => {
  const [error, setError] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [exchangeRates, setExchangeRates] = useState<IExchangeRates>({});
  const { fontColors } = usePanel();

  // convert price from another currency to USD
  const convertToUSD = (input: string, originalCurrency: CurrencyType): string => {
    if (isEmpty(exchangeRates)) return '';
    const convertedPrice =
      Number(input.replace(/,/g, '')) /
      exchangeRates[convertStringToECurrencyType(originalCurrency)]?.ExchangeValueToUSD;
    return isNaN(convertedPrice) ? '' : convertedPrice.toFixed(2);
  };

  // handler when either the selected price or currency changes
  const debouncedOnChange = debounce((newPrice: string, newCurrency: CurrencyType) => {
    onChange({
      price: convertToUSD(newPrice, newCurrency),
      uiPrice: newPrice,
      uiCurrency: newCurrency,
    });
  }, 800);

  // handler when fetching exchange rates fails or return empty
  const onExchangeRatesError = (err: any) => {
    setError(err);
    setMessage('Error retrieving exchange rates. Please reload.');
  };

  // handler when fetching exchange rates succeeds
  const onExchangeRatesSuccess = () => {
    setError(null);
    setMessage('');
  };

  // fetch exchange rates on initial load
  useEffect(() => {
    fetchExchangeRates()
      .then((rates: Array<IExchangeRate>) => {
        if (isEmpty(rates)) {
          onExchangeRatesError({ message: 'No exchange rate available' });
          return;
        }
        setExchangeRates(
          rates.reduce((acc: IExchangeRates, rate: IExchangeRate) => {
            acc[rate.CurrencyType] = rate;
            return acc;
          }, {}),
        );
        onExchangeRatesSuccess();
      })
      .catch((err) => {
        onExchangeRatesError(err);
      });
  }, []);

  return (
    <>
      <Cols
        style={{
          alignItems: 'flex-end',
          maxWidth: isInChannels ? '400px' : undefined,
          justifyContent: isInChannels ? 'space-between' : undefined,
        }}
      >
        <Col style={{ marginRight: '1rem', width: '100%' }}>
          <NumberFormat
            name='price'
            value={price}
            label={isInChannels ? 'Bid Floor' : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              debouncedOnChange(e.target.value, currency);
            }}
            className={className}
            disabled={isDisabled}
            error={isErrored || error}
            customInput={TextField}
            thousandSeparator={true}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {convertStringToCurrencySymbol(currency)}
                </InputAdornment>
              ),
            }}
            style={{ width: '100%', maxWidth: isInChannels ? '320px' : '125px' }}
          />
        </Col>
        <Col>
          <SingleSelect
            label={isInChannels ? undefined : 'Currency'}
            name='Currency'
            onChange={(_, value: string) => {
              debouncedOnChange(price, value);
            }}
            value={currency}
            items={currencies}
            style={{
              color: isDisabled ? fontColors.muted : fontColors.base,
              pointerEvents: isDisabled ? 'none' : 'all',
            }}
          />
        </Col>
      </Cols>

      {/* render price converted to USD if the selected currency is not USD */}
      {!error && currency !== CurrencyTypeUSD && (
        <Text muted extraSmall style={{ marginTop: '0.5em' }}>
          <Span bold muted>
            {`${convertStringToCurrencySymbol(CurrencyTypeUSD)}${convertToUSD(price, currency)} `}
          </Span>
          *FX rate updated every 12 hours
        </Text>
      )}

      {error && (
        <Text extraSmall style={{ marginTop: '0.5em', color: publicaColors.red }}>
          {message}
        </Text>
      )}
    </>
  );
};
